import { useEffect, useRef, useState } from 'react';

import { Checkbox, Chips, Dropdown, FieldLabel, NumericInput, TabPage, TabView, TextInput } from 'fluxo-ui';
import { BarChartIcon, CalendarIcon, EyeIcon, SettingsIcon, UserIcon } from 'fluxo-ui/icons';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface ExamSettingsData {
    // Scheduling
    startDateTime?: string;
    endDateTime?: string;
    duration: number;
    timezone?: string;

    // Access Control
    accessControl: string;
    passcode?: string;
    emailWhitelist?: string[];

    // Attempt Configuration
    maxAttempts: number;
    retakeCooldownMinutes?: number;
    attemptScoringPolicy: string;

    // Navigation
    allowBackwardNavigation: boolean;
    questionAccessMode: string;
    allowBookmarking: boolean;
    showQuestionPalette: boolean;
    autoSaveIntervalSeconds: number;

    // Proctoring
    fullscreenRequired: boolean;
    trackWindowFocus: boolean;
    disableCopyPaste: boolean;
    webcamProctoring: boolean;

    // Grading
    gradingScale: string;
    passingScore: number;
    negativeMarking: boolean;
    negativeMarkingValue?: number;

    // Feedback
    showScoreAfterSubmission: string;
    showCorrectAnswersAfter: string;
    allowExamReview: string;
}

interface ExamSettingsProps {
    settings?: Partial<ExamSettingsData>;
    onChange?: (settings: Partial<ExamSettingsData>) => void;
    disabled?: boolean;
}

const ExamSettings: React.FC<ExamSettingsProps> = ({ settings = {}, onChange, disabled = false }) => {
    const [formData, setFormData] = useState<Partial<ExamSettingsData>>({
        duration: 3600,
        accessControl: 'open_access',
        maxAttempts: 1,
        attemptScoringPolicy: 'highest',
        allowBackwardNavigation: true,
        questionAccessMode: 'random_access',
        allowBookmarking: true,
        showQuestionPalette: true,
        autoSaveIntervalSeconds: 30,
        fullscreenRequired: false,
        trackWindowFocus: false,
        disableCopyPaste: false,
        webcamProctoring: false,
        gradingScale: 'percentage',
        passingScore: 60,
        negativeMarking: false,
        showScoreAfterSubmission: 'immediately',
        showCorrectAnswersAfter: 'after_deadline',
        allowExamReview: 'full_review',
        ...settings,
    });

    // Push the merged defaults up once on mount: otherwise an exam created
    // without touching the settings tab is saved with no duration/scoring at
    // all, even though the UI displays these defaults.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useEffect(() => {
        onChangeRef.current?.(formData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateField = <K extends keyof ExamSettingsData>(field: K, value: ExamSettingsData[K]) => {
        const updated = { ...formData, [field]: value };
        setFormData(updated);
        onChange?.(updated);
    };

    const accessControlOptions: ListItem[] = [
        { label: 'Open Access', value: 'open_access' },
        { label: 'Passcode', value: 'passcode' },
        { label: 'Email Whitelist', value: 'email_whitelist' },
        { label: 'Email OTP', value: 'email_otp' },
        { label: 'Mobile OTP', value: 'mobile_otp' },
    ];

    const scoringPolicyOptions: ListItem[] = [
        { label: 'Latest Attempt', value: 'latest' },
        { label: 'Highest Score', value: 'highest' },
        { label: 'First Attempt', value: 'first' },
        { label: 'Average', value: 'average' },
    ];

    const accessModeOptions: ListItem[] = [
        { label: 'Random Access', value: 'random_access' },
        { label: 'Sequential', value: 'sequential' },
        { label: 'Section Based', value: 'section_based' },
    ];

    const gradingScaleOptions: ListItem[] = [
        { label: 'Percentage', value: 'percentage' },
        { label: 'Points', value: 'points' },
        { label: 'Letter Grades', value: 'letter_grades' },
        { label: 'Pass/Fail', value: 'pass_fail' },
    ];

    const feedbackTimingOptions: ListItem[] = [
        { label: 'Immediately', value: 'immediately' },
        { label: 'After Deadline', value: 'after_deadline' },
        { label: 'After Manual Grading', value: 'after_manual_grading' },
        { label: 'Never', value: 'never' },
    ];

    const reviewOptions: ListItem[] = [
        { label: 'Full Review', value: 'full_review' },
        { label: 'Summary Only', value: 'summary_only' },
        { label: 'No Review', value: 'no_review' },
    ];

    return (
        <TabView className="border border-slate-200 dark:border-slate-700 rounded-lg">
            {/* Scheduling Tab */}
            <TabPage header="Scheduling" leftIcon={CalendarIcon}>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FieldLabel label="Start Date & Time" hint="Leave empty for immediate start">
                            <TextInput
                                type="datetime-local"
                                value={formData.startDateTime || ''}
                                onChange={(e: ComponentEvent<string>) => updateField('startDateTime', e.value)}
                                disabled={disabled}
                            />
                        </FieldLabel>

                        <FieldLabel label="End Date & Time" hint="Leave empty for no end date">
                            <TextInput
                                type="datetime-local"
                                value={formData.endDateTime || ''}
                                onChange={(e: ComponentEvent<string>) => updateField('endDateTime', e.value)}
                                disabled={disabled}
                            />
                        </FieldLabel>
                    </div>

                    <FieldLabel label="Duration (minutes)" hint="Total time limit for the exam">
                        <NumericInput
                            value={Math.floor((formData.duration || 3600) / 60)}
                            onChange={(e: ComponentEvent<number | undefined>) => updateField('duration', (e.value ?? 1) * 60)}
                            min={1}
                            max={600}
                            disabled={disabled}
                        />
                    </FieldLabel>
                </div>
            </TabPage>

            {/* Access Control Tab */}
            <TabPage header="Access Control" leftIcon={UserIcon}>
                <div className="p-6 space-y-4">
                    <FieldLabel label="Access Control Method">
                        <Dropdown
                            options={accessControlOptions}
                            value={formData.accessControl}
                            onChange={(e: ComponentEvent<string>) => updateField('accessControl', e.value)}
                            disabled={disabled}
                        />
                    </FieldLabel>

                    {formData.accessControl === 'passcode' && (
                        <FieldLabel label="Passcode" hint="Students must enter this code to access the exam">
                            <TextInput
                                value={formData.passcode || ''}
                                onChange={(e: ComponentEvent<string>) => updateField('passcode', e.value)}
                                placeholder="Enter passcode"
                                disabled={disabled}
                            />
                        </FieldLabel>
                    )}

                    {formData.accessControl === 'email_whitelist' && (
                        <FieldLabel label="Email Whitelist" hint="Only these email addresses can access the exam">
                            <Chips
                                value={formData.emailWhitelist || []}
                                onChange={(e: ComponentEvent<string[]>) => updateField('emailWhitelist', e.value)}
                                placeholder="Type email and press Enter"
                                disabled={disabled}
                            />
                        </FieldLabel>
                    )}

                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                        <h4 className="font-medium text-slate-900 dark:text-white mb-4">Attempt Configuration</h4>
                        <div className="space-y-4">
                            <FieldLabel label="Maximum Attempts" hint="0 for unlimited">
                                <NumericInput
                                    value={formData.maxAttempts}
                                    onChange={(e: ComponentEvent<number | undefined>) => updateField('maxAttempts', e.value ?? 0)}
                                    min={0}
                                    max={10}
                                    disabled={disabled}
                                />
                            </FieldLabel>

                            {formData.maxAttempts !== 1 && (
                                <>
                                    <FieldLabel label="Retake Cooldown (minutes)">
                                        <NumericInput
                                            value={formData.retakeCooldownMinutes || 0}
                                            onChange={(e: ComponentEvent<number | undefined>) => updateField('retakeCooldownMinutes', e.value)}
                                            min={0}
                                            max={1440}
                                            disabled={disabled}
                                        />
                                    </FieldLabel>

                                    <FieldLabel label="Scoring Policy">
                                        <Dropdown
                                            options={scoringPolicyOptions}
                                            value={formData.attemptScoringPolicy}
                                            onChange={(e: ComponentEvent<string>) => updateField('attemptScoringPolicy', e.value)}
                                            disabled={disabled}
                                        />
                                    </FieldLabel>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </TabPage>

            {/* Navigation Tab */}
            <TabPage header="Navigation & UX" leftIcon={EyeIcon}>
                <div className="p-6 space-y-4">
                    <Checkbox
                        checked={formData.allowBackwardNavigation}
                        onChange={(e: ComponentEvent<boolean>) => updateField('allowBackwardNavigation', e.value)}
                        label="Allow backward navigation (students can go back to previous questions)"
                        disabled={disabled}
                    />

                    <FieldLabel label="Question Access Mode">
                        <Dropdown
                            options={accessModeOptions}
                            value={formData.questionAccessMode}
                            onChange={(e: ComponentEvent<string>) => updateField('questionAccessMode', e.value)}
                            disabled={disabled}
                        />
                    </FieldLabel>

                    <Checkbox
                        checked={formData.allowBookmarking}
                        onChange={(e: ComponentEvent<boolean>) => updateField('allowBookmarking', e.value)}
                        label="Allow students to bookmark questions for review"
                        disabled={disabled}
                    />

                    <Checkbox
                        checked={formData.showQuestionPalette}
                        onChange={(e: ComponentEvent<boolean>) => updateField('showQuestionPalette', e.value)}
                        label="Show question palette (overview of all questions)"
                        disabled={disabled}
                    />

                    <FieldLabel label="Auto-save Interval (seconds)">
                        <NumericInput
                            value={formData.autoSaveIntervalSeconds}
                            onChange={(e: ComponentEvent<number | undefined>) => updateField('autoSaveIntervalSeconds', e.value ?? 30)}
                            min={10}
                            max={300}
                            disabled={disabled}
                        />
                    </FieldLabel>
                </div>
            </TabPage>

            {/* Proctoring Tab */}
            <TabPage header="Proctoring" leftIcon={SettingsIcon}>
                <div className="p-6 space-y-4">
                    <Checkbox
                        checked={formData.fullscreenRequired}
                        onChange={(e: ComponentEvent<boolean>) => updateField('fullscreenRequired', e.value)}
                        label="Require fullscreen mode (violations will be tracked)"
                        disabled={disabled}
                    />

                    <Checkbox
                        checked={formData.trackWindowFocus}
                        onChange={(e: ComponentEvent<boolean>) => updateField('trackWindowFocus', e.value)}
                        label="Track window focus (detect tab switches and window changes)"
                        disabled={disabled}
                    />

                    <Checkbox
                        checked={formData.disableCopyPaste}
                        onChange={(e: ComponentEvent<boolean>) => updateField('disableCopyPaste', e.value)}
                        label="Disable copy/paste"
                        disabled={disabled}
                    />

                    <Checkbox
                        checked={formData.webcamProctoring}
                        onChange={(e: ComponentEvent<boolean>) => updateField('webcamProctoring', e.value)}
                        label="Enable webcam proctoring (requires user permission)"
                        disabled={disabled}
                    />

                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            ⚠️ Note: Strict proctoring settings may affect user experience. Use appropriately based on exam importance.
                        </p>
                    </div>
                </div>
            </TabPage>

            {/* Grading & Feedback Tab */}
            <TabPage header="Grading & Feedback" leftIcon={BarChartIcon}>
                <div className="p-6 space-y-6">
                    <div>
                        <h4 className="font-medium text-slate-900 dark:text-white mb-4">Grading Configuration</h4>
                        <div className="space-y-4">
                            <FieldLabel label="Grading Scale">
                                <Dropdown
                                    options={gradingScaleOptions}
                                    value={formData.gradingScale}
                                    onChange={(e: ComponentEvent<string>) => updateField('gradingScale', e.value)}
                                    disabled={disabled}
                                />
                            </FieldLabel>

                            <FieldLabel label="Passing Score (%)">
                                <NumericInput
                                    value={formData.passingScore}
                                    onChange={(e: ComponentEvent<number | undefined>) => updateField('passingScore', e.value ?? 0)}
                                    min={0}
                                    max={100}
                                    disabled={disabled}
                                />
                            </FieldLabel>

                            <Checkbox
                                checked={formData.negativeMarking}
                                onChange={(e: ComponentEvent<boolean>) => updateField('negativeMarking', e.value)}
                                label="Enable negative marking for incorrect answers"
                                disabled={disabled}
                            />

                            {formData.negativeMarking && (
                                <FieldLabel label="Negative Marking (% of question marks)">
                                    <NumericInput
                                        value={formData.negativeMarkingValue || 25}
                                        onChange={(e: ComponentEvent<number | undefined>) => updateField('negativeMarkingValue', e.value)}
                                        min={0}
                                        max={100}
                                        disabled={disabled}
                                    />
                                </FieldLabel>
                            )}
                        </div>
                    </div>

                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                        <h4 className="font-medium text-slate-900 dark:text-white mb-4">Feedback & Results</h4>
                        <div className="space-y-4">
                            <FieldLabel label="Show Score">
                                <Dropdown
                                    options={feedbackTimingOptions}
                                    value={formData.showScoreAfterSubmission}
                                    onChange={(e: ComponentEvent<string>) => updateField('showScoreAfterSubmission', e.value)}
                                    disabled={disabled}
                                />
                            </FieldLabel>

                            <FieldLabel label="Show Correct Answers">
                                <Dropdown
                                    options={feedbackTimingOptions}
                                    value={formData.showCorrectAnswersAfter}
                                    onChange={(e: ComponentEvent<string>) => updateField('showCorrectAnswersAfter', e.value)}
                                    disabled={disabled}
                                />
                            </FieldLabel>

                            <FieldLabel label="Exam Review">
                                <Dropdown
                                    options={reviewOptions}
                                    value={formData.allowExamReview}
                                    onChange={(e: ComponentEvent<string>) => updateField('allowExamReview', e.value)}
                                    disabled={disabled}
                                />
                            </FieldLabel>
                        </div>
                    </div>
                </div>
            </TabPage>
        </TabView>
    );
};

export default ExamSettings;
